import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Loading from './Loading';
import { baseUrl, getTokenFromLocalStorage } from "./DataServices/Configuration";

/* ================= TYPES ================= */

interface IEngagementDepthBuckets {
  oneToFour: number;
  fiveToEight: number;
  nineToTwelve: number;
  twelvePlus: number;
}

interface IRenterEngagement {
  totalInquiries: number;
  engagedRenters: number;
  engagementRate: number;
  averageThreadSize: number;
  depthBuckets: IEngagementDepthBuckets;
}

interface IKpiData {
  title: string;
  averagePerDayInquiries: number;
  slotBookingPercentage: number;
  slotUtilizationRate: number;
  dailyCountsInquiries: { date: string; count: number }[];
  averageTimeToBookSlot: number;
  renterEngagement: IRenterEngagement;
}

/* ================= COMPONENT ================= */

const KpisPage: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const navigate = useNavigate();

  const [kpiData, setKpiData] = useState<IKpiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* ================= FETCH ================= */

  useEffect(() => {
    const fetchKpis = async () => {
      if (!listingId) return;

      try {
        setLoading(true);
        const token = await getTokenFromLocalStorage();
        if (!token) {
          setError('No authentication token found');
          return;
        }

        const response = await fetch(`${baseUrl}/api/Kpis/${listingId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: IKpiData = await response.json();
        setKpiData(data);
      } catch (err: any) {
        console.error('Error fetching KPIs:', err);
        setError(err.message || 'Failed to load KPIs');
      } finally {
        setLoading(false);
      }
    };

    fetchKpis();
  }, [listingId]);

  /* ================= STATES ================= */

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <Loading />
      </div>
    );
  }

  if (error || !kpiData) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <p className="text-gray-600">{error || 'No data available'}</p>
      </div>
    );
  }

  // Prepare chart data
  const dailyCounts = kpiData.dailyCountsInquiries || [];
  let chartData = dailyCounts
    .filter(day => {
      const date = new Date(day.date);
      return date.getFullYear() >= 2020 && date.getFullYear() <= 2030 && !isNaN(date.getTime());
    })
    .map(day => ({
      originalDate: new Date(day.date),
      date: `${new Date(day.date).getDate()}/${new Date(day.date).getMonth() + 1}`,
      count: day.count
    }))
    .sort((a, b) => a.originalDate.getTime() - b.originalDate.getTime());

  // Ensure the last date is today
  const today = new Date();
  if (chartData.length > 0) {
    const lastDate = chartData[chartData.length - 1].originalDate;
    if (lastDate.toDateString() !== today.toDateString()) {
      chartData.push({
        originalDate: today,
        date: `${today.getDate()}/${today.getMonth() + 1}`,
        count: 0
      });
    }
  }

  // Prepare engagement data
  const total = kpiData.renterEngagement.totalInquiries;
  const engagementData = [
    {
      label: '1–4 emails',
      count: kpiData.renterEngagement.depthBuckets.oneToFour
    },
    {
      label: '5–8 emails',
      count: kpiData.renterEngagement.depthBuckets.fiveToEight
    },
    {
      label: '9–12 emails',
      count: kpiData.renterEngagement.depthBuckets.nineToTwelve
    },
    {
      label: '12+ emails',
      count: kpiData.renterEngagement.depthBuckets.twelvePlus
    }
  ].map(item => ({
    ...item,
    percentage: total > 0
      ? Math.round((item.count / total) * 100)
      : 0
  }));

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{kpiData.title}</h1>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Metrics Summary and Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Metrics Summary */}
          <div className="lg:col-span-1 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Card 1 */}
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h3 className="text-sm font-bold text-gray-700 mb-2">Average Inquiries per Day</h3>
                <p className="text-3xl font-bold text-gray-900">{(kpiData.averagePerDayInquiries || 0).toFixed(1)}</p>
              </div>
              {/* Card 2 */}
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h3 className="text-sm font-bold text-gray-700 mb-2">Slot Booked vs Inquiries</h3>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-green-200 rounded-sm h-4">
                    <div className="bg-green-500 h-4 rounded-sm" style={{ width: `${kpiData.slotBookingPercentage || 0}%` }}></div>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{kpiData.slotBookingPercentage || 0}%</p>
                </div>
              </div>
              {/* Card 3 */}
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h3 className="text-sm font-bold text-gray-700 mb-2">Average Time to Book a Slot</h3>
                <p className="text-3xl font-bold text-gray-900">
                  {(kpiData.averageTimeToBookSlot || 0).toFixed(1)} <span className="text-xl font-normal text-gray-600">hrs</span>
                </p>
              </div>
              {/* Card 4 */}
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h3 className="text-sm font-bold text-gray-700 mb-2">Slot Utilization Percentage</h3>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-orange-200 rounded-sm h-4">
                    <div className="bg-orange-500 h-4 rounded-sm" style={{ width: `${kpiData.slotUtilizationRate || 0}%` }}></div>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{kpiData.slotUtilizationRate || 0}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Chart Section */}
          <div className="lg:col-span-1 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            
            {chartData.length === 0 ? (
              <div className="relative w-full h-96 border border-gray-300 rounded">
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-gray-500">No inquiry data available</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="date" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" domain={[0, Math.max(...chartData.map(d => d.count)) + 1]} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#f9fafb',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      color: '#111827'
                    }}
                  />
                  <Bar dataKey="count" fill="#374151" barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Engagement Depth Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Renter Engagement Depth (Email Thread Size)</h3>
          {total === 0 ? (
            <p className="text-sm text-gray-500">No renter engagement yet (0 / 0)</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {engagementData.map((item, idx) => (
                <div key={idx} className="flex flex-col">
                  <div className="flex justify-between items-center mb-2 ">
                    <span className="text-sm font-medium text-gray-700">{item.label}</span>
                    <span className="text-sm font-semibold text-gray-900">{item.count}/{total}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-md h-8 group">
                    <div className="h-8 rounded-md group-hover:bg-blue-300 transition-colors duration-200" style={{ backgroundColor: '#374151', width: `${item.percentage}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KpisPage;
